type MessageCallback = (data: any) => void
type CloseHandler = (event: CloseEvent) => void

/** Web crypto is missing in React Native Hermes; fall back for RN + older browsers. */
function createHandlerId(): string {
  const c = globalThis.crypto as Crypto | undefined
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0
    const v = ch === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export class WebSocketHandler {
  _client: WebSocketClient
  _path: string
  _id: string
  _callback: MessageCallback | null = null

  constructor(client: WebSocketClient, path: string) {
    this._client = client
    this._path = path
    this._id = createHandlerId()
    client._handlers.set(this._id, this)
  }

  get id() {
    return this._id
  }

  bind(callback: MessageCallback) {
    this._callback = callback
  }

  send(data?: any) {
    const ws = this._client._ws
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not open')
      return
    }

    const payload: any = {
      id: this._id,
      path: this._path,
      meta: { token: this._client._authToken },
    }

    if (data !== undefined) {
      payload.data = data
    }

    ws.send(JSON.stringify(payload))
  }

  close() {
    this._callback = null
    this._client._handlers.delete(this._id)
  }
}

export class WebSocketClient {
  _ws: WebSocket | null = null
  _url: string
  _authToken: string = ''
  _handlers: Map<string, WebSocketHandler> = new Map()
  _closeHandlers: CloseHandler[] = []

  constructor(url: string) {
    this._url = url
  }

  get url() {
    return this._url
  }

  isOpen() {
    return this._ws?.readyState === WebSocket.OPEN
  }

  connect(authToken: string): Promise<void> {
    this._authToken = authToken
    return new Promise((resolve, reject) => {
      // Avoid stacking sockets if connect is called twice
      if (this._ws) {
        this._ws.onopen = null
        this._ws.onmessage = null
        this._ws.onerror = null
        this._ws.onclose = null
        try {
          this._ws.close()
        } catch {
          // ignore
        }
        this._ws = null
      }

      let settled = false
      const ws = new WebSocket(this._url)
      this._ws = ws

      ws.onopen = () => {
        if (settled) return
        settled = true
        resolve()
      }

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(String(event.data))
          if (message.id) {
            const handler = this._handlers.get(message.id)
            handler?._callback?.(message.data)
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err)
        }
      }

      ws.onerror = () => {
        if (settled) return
        settled = true
        reject(new Error(`WebSocket error connecting to ${this._url}`))
      }

      ws.onclose = (event) => {
        if (!settled) {
          settled = true
          reject(
            new Error(
              `WebSocket closed before open (${event.code}${event.reason ? `: ${event.reason}` : ''})`,
            ),
          )
        }
        for (const handler of this._closeHandlers) {
          handler(event)
        }
      }
    })
  }

  with(path: string): WebSocketHandler {
    return new WebSocketHandler(this, path)
  }

  onClose(handler: CloseHandler) {
    this._closeHandlers.push(handler)
  }

  disconnect() {
    if (this._ws) {
      this._ws.onopen = null
      this._ws.onmessage = null
      this._ws.onerror = null
      this._ws.onclose = null
      try {
        this._ws.close()
      } catch {
        // ignore
      }
    }
    this._ws = null
    this._handlers.clear()
    this._closeHandlers = []
  }
}
